/**
 * Course types — simplified video-only capsule model.
 *
 * Courses bundle multiple capsules together. Each capsule has exactly
 * two layers: intro (description + optional video) and chords (video only,
 * with tabs embedded in the video itself).
 */

/**
 * Layer names available in course capsules
 */
export type CourseLayerName = 'intro' | 'chords';

export const COURSE_LAYER_ORDER: CourseLayerName[] = ['intro', 'chords'];

export const COURSE_LAYER_LABELS: Record<CourseLayerName, string> = {
  intro: 'הקדמה',
  chords: 'ליווי',
};

/**
 * Content for a single layer within a course capsule.
 * Simplified: video only, no MusicXML/tabs, no sync offsets.
 */
export interface CourseLayerContent {
  /** Video URL (Cloudinary) */
  video?: string;
  /** Original uploaded video filename */
  originalVideoFileName?: string;
  /** Text description (used by intro layer) */
  description?: string;
  /** Melody audio URL for practice mode (Cloudinary) */
  melodyAudio?: string;
  /** Original uploaded melody audio filename */
  originalMelodyAudioFileName?: string;
  /** Chord progression PDF URL (Cloudinary) */
  chordsPdf?: string;
  /** Original uploaded chords PDF filename */
  originalChordsPdfFileName?: string;
}

/**
 * A single capsule within a course
 */
export interface CourseCapsule {
  /** Unique identifier */
  id: string;
  /** URL-safe slug */
  slug: string;
  /** Display title (song name) */
  title: string;
  /** Artist name */
  artist?: string;
  /** Sort order within the course */
  order: number;
  /** Layer content */
  layers: {
    intro?: CourseLayerContent;
    chords?: CourseLayerContent;
  };
}

/**
 * A course bundling multiple capsules
 */
export interface Course {
  /** Unique identifier */
  id: string;
  /** URL-safe slug */
  slug: string;
  /** Course title */
  title: string;
  /** Course description */
  description?: string;
  /** Primary artist/focus */
  artist?: string;
  /** Ordered capsules */
  capsules: CourseCapsule[];
  /** ISO date string */
  createdAt: string;
  /** ISO date string */
  updatedAt: string;
}

/**
 * Course manifest file structure
 */
export interface CourseManifest {
  courses: Course[];
  totalCount: number;
  generatedAt: string;
}

/**
 * Check if a course capsule has practice materials (melody audio or chords PDF)
 */
export function hasPracticeMaterials(capsule: CourseCapsule): boolean {
  const chords = capsule.layers.chords;
  return Boolean(chords?.melodyAudio || chords?.chordsPdf);
}

/**
 * Helper to get video URL for a course capsule layer
 */
export function getCourseLayerVideoUrl(
  capsule: CourseCapsule,
  layer: CourseLayerName
): string | null {
  const content = capsule.layers[layer];
  return content?.video ?? null;
}

/**
 * Helper to check if a course capsule layer has a video
 */
export function courseLayerHasVideo(
  capsule: CourseCapsule,
  layer: CourseLayerName
): boolean {
  return Boolean(capsule.layers[layer]?.video);
}

/**
 * Get available layers for a course capsule
 */
export function getAvailableCourseLayers(capsule: CourseCapsule): CourseLayerName[] {
  return COURSE_LAYER_ORDER.filter(
    (layer) => capsule.layers[layer] !== undefined
  );
}

/**
 * Parse a compound course submission slug.
 * Format: "course:{courseSlug}/{capsuleSlug}"
 * Returns null if the slug is not a course submission.
 */
export function parseCourseSubmissionSlug(
  slug: string
): { courseSlug: string; capsuleSlug: string } | null {
  if (!slug.startsWith('course:')) return null;
  const rest = slug.slice('course:'.length);
  const slashIdx = rest.indexOf('/');
  if (slashIdx === -1) return null;
  return {
    courseSlug: rest.slice(0, slashIdx),
    capsuleSlug: rest.slice(slashIdx + 1),
  };
}

/**
 * Build a compound course submission slug.
 */
export function buildCourseSubmissionSlug(
  courseSlug: string,
  capsuleSlug: string
): string {
  return `course:${courseSlug}/${capsuleSlug}`;
}
